import type {
  EvaluationContext,
  EvaluationContextValue,
  JsonValue,
  Provider,
  ProviderMetadata,
  ResolutionDetails,
} from '@openfeature/web-sdk';
import { StandardResolutionReasons } from '@openfeature/web-sdk';
import { StatsigClient } from '@statsig/js-client';

/**
 * Name of the string parameter each experiment group must define, set equal to
 * the group (alternative) name. Kept in sync with the backend provider
 * (`libs/common-infrastructure/.../statsig-openfeature-provider.ts`).
 */
// eslint-disable-next-line i18next/no-literal-string -- Statsig experiment parameter name
const EXPERIMENT_ALTERNATIVE_PARAM = 'alternative';

/**
 * OpenFeature Provider that delegates to Statsig.
 *
 * Mapping convention (must match the backend provider):
 * - Boolean flags → Statsig Feature Gate (checkGate)
 * - String flags → Statsig **Experiment**; the resolved value is the assigned
 *   alternative, read from each group's `alternative` parameter (falling back to
 *   the group name if that parameter is absent).
 * - Number/Object flags → Statsig Dynamic Config, reading a parameter named `value`
 *
 * User context flows through `onContextChange` — when the OpenFeature context updates
 * (e.g., user switches workspace), the Statsig client is updated with the new user.
 */
export class StatsigWebProvider implements Provider {
  readonly metadata: ProviderMetadata = { name: 'statsig-web' };

  constructor(private readonly statsigClient: StatsigClient) {}

  async onContextChange(
    _oldContext: EvaluationContext,
    newContext: EvaluationContext,
  ): Promise<void> {
    await this.statsigClient.updateUserAsync(toStatsigUser(newContext));
  }

  resolveBooleanEvaluation(flagKey: string): ResolutionDetails<boolean> {
    const value = this.statsigClient.checkGate(flagKey);
    return { value };
  }

  resolveStringEvaluation(flagKey: string, defaultValue: string): ResolutionDetails<string> {
    const experiment = this.statsigClient.getExperiment(flagKey);
    // Prefer the explicit `alternative` parameter; fall back to the assigned
    // group name when it is missing/empty (a real alternative is never empty),
    // so an arm without the param is not silently reported as the default.
    const fromParam = experiment.get<string>(EXPERIMENT_ALTERNATIVE_PARAM, '');
    const alternative = fromParam !== '' ? fromParam : (experiment.groupName ?? defaultValue);

    return {
      value: alternative,
      variant: alternative,
      reason: isEnrolledExperiment(experiment)
        ? StandardResolutionReasons.TARGETING_MATCH
        : StandardResolutionReasons.DEFAULT,
    };
  }

  resolveNumberEvaluation(flagKey: string, defaultValue: number): ResolutionDetails<number> {
    const config = this.statsigClient.getDynamicConfig(flagKey);
    // eslint-disable-next-line i18next/no-literal-string -- Statsig dynamic-config field name
    return { value: config.get('value', defaultValue) };
  }

  resolveObjectEvaluation<T extends JsonValue>(
    flagKey: string,
    defaultValue: T,
  ): ResolutionDetails<T> {
    const config = this.statsigClient.getDynamicConfig(flagKey);
    // eslint-disable-next-line i18next/no-literal-string -- Statsig dynamic-config field name
    return { value: config.get('value', defaultValue) as T };
  }
}

/**
 * Whether the unit was allocated to an experiment group (vs. falling through to
 * the default because it is outside the experiment). Mirrors the backend
 * provider: prefers the group name and falls back to the rule id.
 */
function isEnrolledExperiment(experiment: { groupName: string | null; ruleID: string }): boolean {
  if (experiment.groupName !== null) {
    return true;
  }
  const ruleId = experiment.ruleID;
  return ruleId !== '' && ruleId !== 'default' && ruleId !== 'disabled';
}

function asString(value: EvaluationContextValue | undefined): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function toStatsigUser(context: EvaluationContext) {
  const { targetingKey, email, appVersion, organizationId, workspaceId, ...rest } = context;
  const emailStr = asString(email);
  const appVersionStr = asString(appVersion);
  const organizationIdStr = asString(organizationId);
  const workspaceIdStr = asString(workspaceId);

  // Custom unit IDs (must match the Custom Unit ID names registered in the
  // Statsig console) so experiments can randomize per workspace or organization,
  // not just per user. Kept in sync with the backend provider.
  const customIDs: Record<string, string> = {};
  if (workspaceIdStr) customIDs.workspaceID = workspaceIdStr;
  if (organizationIdStr) customIDs.organizationID = organizationIdStr;

  return {
    userID: targetingKey,
    ...(emailStr ? { email: emailStr } : {}),
    ...(appVersionStr ? { appVersion: appVersionStr } : {}),
    ...(Object.keys(customIDs).length > 0 ? { customIDs } : {}),
    custom: {
      ...(organizationIdStr ? { organizationId: organizationIdStr } : {}),
      ...(workspaceIdStr ? { workspaceId: workspaceIdStr } : {}),
      ...rest,
    },
  };
}
